"use client";
import React, { useState, useEffect, useRef } from "react";
import {
  Edit,
  Trash2,
  X,
  Search,
  Plus,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useProjectType } from "../../context/ProjectTypeContext";

export default function ProjectTypes() {
  const {
    projectTypes,
    createProjectType,
    updateProjectType,
    deleteProjectType,
  } = useProjectType();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id || null;

  // ── FILTER ─────────────────────────────
  const filteredProjects = projectTypes.filter((p) => {
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      p.name?.toLowerCase().includes(term) ||
      p.description?.toLowerCase().includes(term);

    const matchesFilter =
      filterStatus === "All" ||
      p.status?.toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredProjects.length / rowsPerPage) || 1;

  const paginatedProjects = filteredProjects.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus]);

  // ── CREATE ─────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    const res = await createProjectType({
      name: formData.name,
      description: formData.description,
      status: formData.status,
      create_audit_id: userId,
    });

    if (res.status === 201) {
      setShowCreateModal(false);
      setFormData({ name: "", description: "", status: "active" });
    }
  };

  // ── UPDATE ─────────────────────────────
  const handleEdit = async (e) => {
    e.preventDefault();
    const res = await updateProjectType(selectedProject.id, {
      name: formData.name,
      description: formData.description,
      status: formData.status,
      update_audit_id: userId,
    });

    if (res.status === 200) setShowEditModal(false);
  };

  // ── DELETE ─────────────────────────────
  const handleDelete = async () => {
    const res = await deleteProjectType(selectedProject.id, {
      delete_audit_id: userId,
    });

    if (res.status === 200) setShowDeleteModal(false);
  };

  // ── UI ─────────────────────────────
  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-6 py-6 sm:px-9 max-sm:px-4">

        {/* TITLE */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
          Project Types
        </h1>

        {/* FILTER BAR */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">

          <div className="flex flex-wrap items-center gap-3 w-full">

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>

            <div className="relative flex-1 min-w-[210px]">
              <Search className="absolute left-2.5 top-2.5 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search project types..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 pl-9 text-sm w-full bg-white outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
              />
            </div>

            {(searchTerm || filterStatus !== "All") && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("All");
                  setPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>

          <button
            onClick={() => {
              setFormData({ name: "", description: "", status: "active" });
              setShowCreateModal(true);
            }}
            className="text-white px-10 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
          >
            <Plus size={14} className="inline mr-1" />
            Create
          </button>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] table-auto text-sm">
              <thead>
                <tr className="bg-sky-100 text-gray-700">
                  <th className="py-3 px-4 text-left whitespace-nowrap w-16">ID</th>
                  <th className="py-3 px-4 text-left whitespace-nowrap">Name</th>
                  <th className="py-3 px-4 text-left w-full">Description</th>
                  <th className="py-3 px-4 text-left whitespace-nowrap">Status</th>
                  <th className="py-3 px-4 text-left whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedProjects.length > 0 ? (
                  paginatedProjects.map((p, i) => (
                    <tr
                      key={p.id}
                      className={`border-b hover:bg-gray-50 transition ${
                        i % 2 === 1 ? "bg-[#f7f6fe]" : ""
                      }`}
                    >
                      <td className="py-3 px-4 text-indigo-600 align-top">
                        {(page - 1) * rowsPerPage + i + 1}
                      </td>

                      <td className="py-3 px-4 whitespace-nowrap align-top">
                        {p.name}
                      </td>

                      <td className="py-3 px-4 align-top">
                        <DescriptionCell text={p.description} />
                      </td>

                      <td className="py-3 px-4 align-top">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            p.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>

                      <td className="py-3 px-4 flex gap-3 text-gray-600 align-top">
                        <Edit
                          size={16}
                          className="cursor-pointer hover:text-indigo-600"
                          onClick={() => {
                            setSelectedProject(p);
                            setFormData(p);
                            setShowEditModal(true);
                          }}
                        />

                        <Trash2
                          size={16}
                          className="cursor-pointer hover:text-red-600"
                          onClick={() => {
                            setSelectedProject(p);
                            setShowDeleteModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-6 text-gray-500">
                      No project types found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION (same as Projects) ── */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">

            {/* Rows per page */}
            <div className="flex items-center space-x-2">
              <label className="text-gray-700 text-sm">Rows:</label>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[3, 5, 10, 20].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {/* Nav buttons + dynamic page numbers */}
            <div className="flex items-center space-x-1">

              {/* First page */}
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>

              {/* Previous */}
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowLeft size={20} />
              </button>

              {/* Dynamic page number buttons */}
              {(() => {
                const pages = [];
                let startPage = Math.max(page - 1, 1);
                let endPage = Math.min(startPage + 2, totalPages);
                if (endPage - startPage < 2) {
                  startPage = Math.max(endPage - 2, 1);
                }
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === page
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              {/* Next */}
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowRight size={20} />
              </button>

              {/* Last page */}
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* ───────── MODALS ───────── */}

      {/* CREATE MODAL */}
      {showCreateModal && (
        <Modal title="Add Project Type" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <Input label="Name" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <TextArea label="Description" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <Status formData={formData} setFormData={setFormData} />
            <Actions onClose={() => setShowCreateModal(false)} />
          </form>
        </Modal>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <Modal title="Edit Project Type" onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <Input label="Name" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <TextArea label="Description" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <Status formData={formData} setFormData={setFormData} />
            <Actions onClose={() => setShowEditModal(false)} />
          </form>
        </Modal>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <Modal title="Delete Project Type" onClose={() => setShowDeleteModal(false)}>
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete{" "}
            <b>{selectedProject?.name}</b>?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 border rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ───────── MODAL ───────── */
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl relative">
        <button onClick={onClose} className="absolute top-3 right-3">
          <X size={18} />
        </button>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/* ───────── INPUTS ───────── */
const Input = ({ label, ...props }) => (
  <div>
    <label className="text-sm">{label}</label>
    <input
      {...props}
      className="w-full border rounded-lg px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
    />
  </div>
);

const TextArea = ({ label, ...props }) => (
  <div>
    <label className="text-sm">{label}</label>
    <textarea
      {...props}
      rows={3}
      className="w-full border rounded-lg px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
    />
  </div>
);

/* ───────── STATUS ───────── */
function Status({ formData, setFormData }) {
  return (
    <div>
      <label className="text-sm">Status</label>
      <div className="flex gap-4 mt-1">
        {["active", "inactive"].map((s) => (
          <label key={s} className="flex gap-2">
            <input
              type="radio"
              checked={formData.status === s}
              onChange={() => setFormData({ ...formData, status: s })}
              className="accent-[#8c57ff]"
            />
            {s}
          </label>
        ))}
      </div>
    </div>
  );
}

/* ───────── ACTIONS ───────── */
const Actions = ({ onClose }) => (
  <div className="flex justify-end gap-3 pt-3">
    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
      Cancel
    </button>
    <button
      type="submit"
      className="px-4 py-2 text-white rounded-lg"
      style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
    >
      Save
    </button>
  </div>
);

/* ───────── DESCRIPTION CELL (with Read More) ───────── */
function DescriptionCell({ text }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [text]);

  return (
    <div>
      <p
        ref={textRef}
        className={`${expanded ? "" : "line-clamp-2"} break-words whitespace-normal max-w-[480px]`}
      >
        {text}
      </p>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-indigo-600 text-xs font-medium mt-1 hover:underline"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}